import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Check } from "lucide-react";
import { growth, formatError } from "@/lib/growthApi";
import { GROWTH_SERVICES, CALENDLY_URL } from "@/content";

const INPUT = "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white outline-none transition-colors focus:border-gold/60 placeholder:text-slate-600";
const LABEL = "mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500";
const FIELDS = [
  ["full_name", "Full name", "text", true],
  ["company", "Company", "text", true],
  ["job_title", "Job title", "text", true],
  ["email", "Business email", "email", true],
  ["telephone", "Telephone (optional)", "tel", false],
  ["website", "Company website", "text", false],
  ["country", "Country", "text", true],
];

export default function LeadCapture({ sessionId, defaults = {}, onDone }) {
  const [v, setV] = useState({ full_name: "", company: "", job_title: "", email: "", telephone: "", website: "", country: "", area_of_interest: "", commercial_challenge: "", preferred_contact: "Email", consent: false, ...defaults });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!v.consent) return toast.error("Please confirm you are happy for Thrive to contact you.");
    setBusy(true);
    try {
      await growth.lead({ ...v, session_id: sessionId });
      setSent(true);
      onDone?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-gold/40 bg-navy-light/40 p-8" data-testid="lead-success">
        <div className="flex items-center gap-3 text-gold"><Check size={16} /><span className="font-mono text-[11px] uppercase tracking-[0.25em]">Received</span></div>
        <h3 className="mt-4 font-display text-xl font-bold tracking-[-0.03em] text-white">Thank you. Thrive will take a closer look.</h3>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">A director will review the conversation and come back to you by your preferred method. If you would rather talk sooner, you can book a call directly.</p>
        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="lead-success-book-call" className="group mt-6 inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow">
          Talk to Thrive <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-white/10 bg-navy-light/40 p-6 sm:p-8" data-testid="lead-capture-form">
      <p className="eyebrow mb-3">Closer Look</p>
      <h3 className="font-display text-xl sm:text-2xl font-bold tracking-[-0.03em] text-white">Would you like Thrive to take a closer look at this opportunity?</h3>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">Share your details and a Thrive director will review this conversation and respond personally.</p>
      <div className="mt-8 grid gap-5 sm:grid-cols-2">
        {FIELDS.map(([k, label, type, req]) => (
          <div key={k}>
            <label htmlFor={`lead-${k}`} className={LABEL}>{label}</label>
            <input id={`lead-${k}`} data-testid={`lead-${k}-input`} type={type} required={req} value={v[k]} onChange={set(k)} className={INPUT} />
          </div>
        ))}
        <div>
          <label htmlFor="lead-area" className={LABEL}>Area of interest</label>
          <select id="lead-area" data-testid="lead-area-select" value={v.area_of_interest} onChange={set("area_of_interest")} className={`${INPUT} bg-navy`}>
            <option value="">Select</option>
            {GROWTH_SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="lead-challenge" className={LABEL}>Commercial challenge</label>
          <textarea id="lead-challenge" data-testid="lead-challenge-input" rows={3} value={v.commercial_challenge} onChange={set("commercial_challenge")} className={INPUT} placeholder="In a sentence or two, what are you trying to achieve?" />
        </div>
        <div>
          <label htmlFor="lead-contact" className={LABEL}>Preferred contact method</label>
          <select id="lead-contact" data-testid="lead-contact-select" value={v.preferred_contact} onChange={set("preferred_contact")} className={`${INPUT} bg-navy`}>
            {["Email", "Telephone", "Video call"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      </div>
      <label className="mt-7 flex items-start gap-3 text-xs leading-relaxed text-slate-400">
        <input type="checkbox" data-testid="lead-consent-checkbox" checked={v.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-[#D4AF37]" />
        <span>By submitting your details, you agree that Thrive Business Development may contact you regarding your enquiry. Your details are stored only for this purpose and are not shared with third parties.</span>
      </label>
      <button type="submit" disabled={busy} data-testid="lead-submit-button" className="group mt-7 inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all hover:bg-gold-glow disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : null}
        {busy ? "Sending" : "Ask Thrive to take a closer look"}
        {!busy && <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />}
      </button>
    </form>
  );
}
